import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Search, UserPlus, Phone, MapPin, FileText, Info } from 'lucide-react';
import { Badge } from '../components/ui/badge';

const Remitentes = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [cedulaMatch, setCedulaMatch] = useState(null);
  const [formData, setFormData] = useState({
    nombre_completo: '',
    cedula_pasaporte: '',
    tipo_documento: 'SA13-CED',
    telefono: '',
    telefono_alternativo: '',
    direccion_completa: '',
    ciudad: '',
    estado: '',
    pais: 'USA',
    tipo: 'remitente',
    email: ''
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers', { params: { tipo: 'remitente' } });
      setCustomers(response.data.data || []);
    } catch (error) {
      toast.error('Error al cargar remitentes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        await api.put(`/customers/${editingCustomer.id}`, formData);
        toast.success('Remitente actualizado exitosamente');
      } else {
        await api.post('/customers', formData);
        toast.success('Remitente creado exitosamente');
      }
      fetchCustomers();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData(customer);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este remitente?')) return;
    try {
      await api.delete(`/customers/${id}`);
      toast.success('Remitente eliminado');
      fetchCustomers();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const resetForm = () => {
    setEditingCustomer(null);
    setCedulaMatch(null);
    setFormData({
      nombre_completo: '',
      cedula_pasaporte: '',
      tipo_documento: 'SA13-CED',
      telefono: '',
      telefono_alternativo: '',
      direccion_completa: '',
      ciudad: '',
      estado: '',
      pais: 'USA',
      tipo: 'remitente',
      email: ''
    });
  };

  const checkCedulaOtherType = async (cedula) => {
    if (!cedula || cedula.length < 3 || editingCustomer) return;
    try {
      const res = await api.get(`/customers/check-cedula/${cedula}`, { params: { current_type: 'remitente' } });
      if (res.data.exists) {
        setCedulaMatch(res.data.customer);
      } else {
        setCedulaMatch(null);
      }
    } catch { setCedulaMatch(null); }
  };

  const applyMatchData = () => {
    if (!cedulaMatch) return;
    setFormData(prev => ({
      ...prev,
      nombre_completo: cedulaMatch.nombre_completo || prev.nombre_completo,
      telefono: cedulaMatch.telefono || prev.telefono,
      telefono_alternativo: cedulaMatch.telefono_alternativo || prev.telefono_alternativo,
      email: cedulaMatch.email || prev.email
    }));
    toast.success('Datos del destinatario aplicados');
  };

  const filteredCustomers = customers.filter(c =>
    c.nombre_completo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.cedula_pasaporte?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.telefono?.includes(searchTerm)
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 p-3 sm:p-6 pb-24">
      <div className="max-w-7xl mx-auto">
        <Card className="shadow-xl border-2 border-green-100">
          <CardHeader className="bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-t-lg px-4 py-4 sm:px-6">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-2 bg-white/20 rounded-lg flex-shrink-0">
                  <UserPlus className="h-6 w-6 sm:h-8 sm:w-8" />
                </div>
                <div className="min-w-0">
                  <CardTitle className="text-xl sm:text-2xl font-bold">Remitentes</CardTitle>
                  <p className="text-green-100 text-xs sm:text-sm truncate">Clientes que envian paquetes</p>
                </div>
              </div>
              <Badge variant="secondary" className="text-sm sm:text-lg px-2 sm:px-4 py-1 sm:py-2 flex-shrink-0">
                {customers.length}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-3 sm:p-6">
            <div className="mb-4 flex flex-col sm:flex-row gap-2 sm:gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <Input placeholder="Buscar por nombre, cedula o telefono..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 h-12 text-base border-2 focus:border-green-500" />
              </div>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={resetForm} className="h-12 px-4 sm:px-6 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-lg w-full sm:w-auto" size="lg">
                    <Plus className="mr-2 h-5 w-5" /> Nuevo Remitente
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-2xl flex items-center gap-2">
                      <UserPlus className="h-6 w-6 text-green-600" />
                      {editingCustomer ? 'Editar Remitente' : 'Nuevo Remitente'}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Label className="text-base font-semibold flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Nombre Completo *
                        </Label>
                        <Input
                          value={formData.nombre_completo}
                          onChange={(e) => setFormData({...formData, nombre_completo: e.target.value})}
                          className="h-11 mt-1"
                          required
                        />
                      </div>
                      
                      <div>
                        <Label className="text-base font-semibold">Tipo de Documento</Label>
                        <Select value={formData.tipo_documento} onValueChange={(value) => setFormData({...formData, tipo_documento: value})}>
                          <SelectTrigger className="h-11 mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="SA13-CED">Cédula</SelectItem>
                            <SelectItem value="SA13-PAS">Pasaporte</SelectItem>
                            <SelectItem value="SA13-LIC">Licencia</SelectItem>
                            <SelectItem value="SA13-TIN">TIN</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label className="text-base font-semibold">Cédula/Pasaporte *</Label>
                        <Input
                          value={formData.cedula_pasaporte}
                          onChange={(e) => {
                            setFormData({...formData, cedula_pasaporte: e.target.value});
                            setCedulaMatch(null);
                          }}
                          onBlur={(e) => checkCedulaOtherType(e.target.value)}
                          className="h-11 mt-1"
                          required
                        />
                        {cedulaMatch && (
                          <div data-testid="cedula-match-alert" className="mt-2 p-3 bg-blue-50 border-2 border-blue-300 rounded-lg">
                            <div className="flex items-start gap-2">
                              <Info className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
                              <div className="flex-1">
                                <p className="text-sm font-bold text-blue-800">Este cliente ya existe como destinatario</p>
                                <p className="text-sm text-blue-700 mt-1">{cedulaMatch.nombre_completo} - Tel: {cedulaMatch.telefono}</p>
                                <Button
                                  type="button"
                                  data-testid="apply-match-data-btn"
                                  variant="outline"
                                  size="sm"
                                  className="mt-2 border-blue-400 text-blue-700 hover:bg-blue-100"
                                  onClick={applyMatchData}
                                >
                                  Usar datos del destinatario
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div>
                        <Label className="text-base font-semibold flex items-center gap-2">
                          <Phone className="h-4 w-4" />
                          Teléfono *
                        </Label>
                        <Input
                          value={formData.telefono}
                          onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                          className="h-11 mt-1"
                          required
                        />
                      </div>
                      
                      <div>
                        <Label className="text-base font-semibold">Teléfono Alternativo</Label>
                        <Input
                          value={formData.telefono_alternativo}
                          onChange={(e) => setFormData({...formData, telefono_alternativo: e.target.value})}
                          className="h-11 mt-1"
                        />
                      </div>
                      
                      <div className="col-span-2">
                        <Label className="text-base font-semibold flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          Dirección Completa *
                        </Label>
                        <Input
                          value={formData.direccion_completa}
                          onChange={(e) => setFormData({...formData, direccion_completa: e.target.value})}
                          className="h-11 mt-1"
                          required
                        />
                      </div>
                      
                      <div>
                        <Label className="text-base font-semibold">Ciudad *</Label>
                        <Input
                          value={formData.ciudad}
                          onChange={(e) => setFormData({...formData, ciudad: e.target.value})}
                          className="h-11 mt-1"
                          required
                        />
                      </div>
                      
                      <div>
                        <Label className="text-base font-semibold">Estado</Label>
                        <Input
                          value={formData.estado}
                          onChange={(e) => setFormData({...formData, estado: e.target.value})}
                          placeholder="Florida, New York, etc."
                          className="h-11 mt-1"
                        />
                      </div>
                      
                      <div className="col-span-2">
                        <Label className="text-base font-semibold">Email</Label>
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          className="h-11 mt-1"
                        />
                      </div>
                    </div>
                    
                    <div className="flex justify-end space-x-3 pt-4 border-t">
                      <Button type="button" variant="outline" size="lg" onClick={() => setDialogOpen(false)}>
                        Cancelar
                      </Button>
                      <Button type="submit" size="lg" className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700">
                        {editingCustomer ? 'Actualizar' : 'Crear Remitente'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Cargando remitentes...</p>
              </div>
            ) : (
              <>
                {/* Mobile Cards */}
                <div className="sm:hidden space-y-3">
                  {filteredCustomers.length === 0 ? (
                    <div className="text-center py-12">
                      <UserPlus className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg">No hay remitentes</p>
                      <p className="text-gray-400 text-sm mt-2">Toca "Nuevo Remitente" para agregar</p>
                    </div>
                  ) : filteredCustomers.map((customer) => (
                    <div key={customer.id} className="bg-white rounded-xl border-2 border-gray-100 p-4 space-y-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-bold text-gray-900 text-base">{customer.nombre_completo}</p>
                          <p className="text-sm text-gray-400">{customer.cedula_pasaporte}</p>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(customer)} className="h-9 w-9 hover:bg-blue-50">
                            <Edit className="h-4 w-4 text-blue-600" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(customer.id)} className="h-9 w-9 hover:bg-red-50">
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                        <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5 text-gray-400" />{customer.telefono}</span>
                        <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-gray-400" />{customer.ciudad}{customer.estado ? `, ${customer.estado}` : ''}</span>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Desktop Table */}
                <div className="hidden sm:block bg-white rounded-lg border-2 border-gray-100 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gradient-to-r from-green-50 to-emerald-50">
                      <TableHead className="font-bold text-gray-900">Nombre</TableHead>
                      <TableHead className="font-bold text-gray-900">Cédula</TableHead>
                      <TableHead className="font-bold text-gray-900">Teléfono</TableHead>
                      <TableHead className="font-bold text-gray-900">Ciudad</TableHead>
                      <TableHead className="font-bold text-gray-900">Estado</TableHead>
                      <TableHead className="font-bold text-gray-900 text-center">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCustomers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-12">
                          <UserPlus className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                          <p className="text-gray-500 text-lg">No hay remitentes registrados</p>
                          <p className="text-gray-400 text-sm mt-2">Haz clic en "Nuevo Remitente" para agregar uno</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredCustomers.map((customer) => (
                        <TableRow key={customer.id} className="hover:bg-green-50/50 transition-colors">
                          <TableCell className="font-medium">{customer.nombre_completo}</TableCell>
                          <TableCell>{customer.cedula_pasaporte}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4 text-gray-400" />
                              {customer.telefono}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4 text-gray-400" />
                              {customer.ciudad}
                            </div>
                          </TableCell>
                          <TableCell>{customer.estado || '-'}</TableCell>
                          <TableCell>
                            <div className="flex justify-center space-x-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEdit(customer)}
                                className="hover:bg-blue-50"
                              >
                                <Edit className="h-4 w-4 text-blue-600" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(customer.id)}
                                className="hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4 text-red-600" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Remitentes;
